import styles from './Login.module.scss';

import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { ArrowBack } from '@mui/icons-material';
import {
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import * as ReactSentry from '@sentry/react';
import md5 from 'md5';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Path, useSearchParams } from 'react-router-dom';
import { useRxDB } from 'rxdb-hooks';

import { ReactComponent as CircleNotch } from '@work4all/assets/icons/circle_notch.svg';

import { FixedLengthInput } from '@work4all/components/lib/input/fixed-length-input/FixedLengthInput';
import {
  TakeRestHeight,
  YSplit,
} from '@work4all/components/lib/layout/y-split';

import {
  components,
  httpClient,
  HttpClientResponse,
  IUser,
  Navigate,
  useFormPlus,
  useLocation,
  useUser,
} from '@work4all/data';
import { USER_META_ID } from '@work4all/data/lib/db';

import { MfaMode } from '@work4all/models/lib/Enums/MfaMode.enum';
import { UserClass } from '@work4all/models/lib/Enums/UserClass.enum';

import { canAddUser } from '@work4all/utils/lib/permissions';

import Bubble from '../../assets/icons/Bubble_4.svg?url';
import { MfaButtons } from '../../components/user-menu/views/security/Security';
import { updateApiLicenseLock } from '../../hooks/use-api-license-lock';
import { useDeepLinking } from '../../hooks/useDeepLinking';
import { ApplicationName } from '../../utils/ApplicationName.enum';
import { API_LICENSE_HEADER_NAME } from '../app-updates/constants';
import { ControllerPlus } from '../mask-overlays/form-plus/controller-plus';
import { BaseActionButton } from '../mask-overlays/locked-inputs';

import { ErrorMessage } from './components/error-message/ErrorMessage';
import { TextField } from './components/text-field';
import { usePerformLogin } from './hooks/use-perform-login';
import { useRestMfa } from './hooks/use-rest-mfa';
import { getAutodiscoveryBaseUrl } from './utils/getAutodiscoveryBaseUrl';

interface IForm {
  apiUrl?: string;
  password: string;
}

const initialValues: LoginFormValue = {
  username: '',
  password: '',
  apiUrl: '',
};

const mfaModeByIdx = {
  0: MfaMode.NORMAL,
  1: MfaMode.TOKEN,
  2: MfaMode.MAIL,
};

export type LoginFormValue = components['schemas']['AuthRequestV2'] & IForm;

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();

  const form = useFormPlus<LoginFormValue>({
    defaultValues: initialValues,
  });

  useDeepLinking();
  const { control, handleSubmit } = form;

  return (
    <LoginPageWrapper>
      {({
        onSubmit,
        loading,
        error,
        setAutoDiscovery,
        autodiscovery,
        mfaMode,
        onMfaCancel,
        onMfaSubmit,
      }) => {
        const handleMfaChange = async (
          e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
        ) => {
          if (e.target.value.length >= 6) {
            e.target.setSelectionRange(0, 0);
            e.target.blur();
            e.target.disabled = true;
            await onMfaSubmit(e.target.value);
            e.target.disabled = false;
          }
        };

        return (
          <>
            <img className={styles['login__bubble-icon']} src={Bubble} alt="" />
            {!mfaMode && (
              <>
                <div className={styles.titleWrapper}>
                  <Typography
                    className={styles['login__title']}
                    component="h4"
                    variant="h4"
                  >
                    {t('LOGIN.WELCOME')}
                  </Typography>
                </div>
                <FormProvider {...form}>
                  <form
                    className={styles['login__form']}
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    {error && (
                      <ErrorMessage
                        data-test-id="login-error-message"
                        message={
                          t(`LOGIN.MESSAGE.${error.type}`, error.variables) ||
                          t('LOGIN.WRONG_PASSWORD')
                        }
                      />
                    )}

                    <ControllerPlus
                      control={control}
                      name="username"
                      render={({ field }) => {
                        return (
                          <TextField
                            required
                            type="email"
                            value={field.value}
                            label={t('LOGIN.EMAIL')}
                            onChange={field.onChange}
                          />
                        );
                      }}
                    />

                    <ControllerPlus
                      control={control}
                      name="password"
                      render={({ field }) => {
                        return (
                          <TextField
                            required
                            type="password"
                            value={field.value}
                            label={t('LOGIN.PASSWORD')}
                            onChange={field.onChange}
                          />
                        );
                      }}
                    />

                    {!autodiscovery && (
                      <ControllerPlus
                        control={control}
                        name="apiUrl"
                        render={({ field }) => {
                          return (
                            <TextField
                              required
                              value={field.value}
                              label={t('LOGIN.API_URL')}
                              onChange={field.onChange}
                              onClear={() => {
                                field.onChange('');
                              }}
                            />
                          );
                        }}
                      />
                    )}

                    <ButtonBase className={styles['autodiscovery-controller']}>
                      <FormControlLabel
                        classes={{
                          root: styles['autodiscovery-controller__label-root'],
                          label: styles['autodiscovery-controller__label'],
                        }}
                        checked={autodiscovery}
                        onChange={() => setAutoDiscovery(!autodiscovery)}
                        value="autodiscovery"
                        control={
                          <Checkbox
                            classes={{
                              root: styles[
                                'autodiscovery-controller__checkbox'
                              ],
                              checked:
                                styles[
                                  'autodiscovery-controller__checkbox--checked'
                                ],
                            }}
                            size="small"
                          />
                        }
                        label={t('LOGIN.API_AUTODETECT')}
                      />
                    </ButtonBase>
                    <Button
                      type="submit"
                      className={styles['login__submit-btn']}
                      variant="text"
                      color="secondary"
                    >
                      <span className={styles['login__submit-btn-label']}>
                        {loading ? (
                          <CircleNotch className={styles.circle} />
                        ) : (
                          t('LOGIN.LOGIN')
                        )}
                      </span>
                    </Button>
                  </form>
                </FormProvider>
              </>
            )}
            {mfaMode && (
              <>
                <div className={styles.titleWrapper}>
                  <Typography
                    className={styles['login__title']}
                    component="h4"
                    variant="h4"
                  >
                    {t('LOGIN.MFA.TITLE')}
                  </Typography>
                </div>
                <Typography
                  variant="body1"
                  className={styles['login__subtitle']}
                >
                  {t('LOGIN.MFA.DESC.' + mfaMode)}
                </Typography>

                <FixedLengthInput
                  length={6}
                  onChange={handleMfaChange}
                  autoFocus
                />

                <Stack className={styles['login__demo-container']}>
                  <BaseActionButton
                    className={styles['login__demo-link']}
                    onClick={onMfaCancel}
                    icon={<ArrowBack />}
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      className={styles['login__demo-text']}
                    >
                      {t('LOGIN.MFA.BACK')}
                    </Typography>
                  </BaseActionButton>
                </Stack>
              </>
            )}
          </>
        );
      }}
    </LoginPageWrapper>
  );
};

interface LoginPageWrapperProps {
  children: (props: {
    onSubmit: (values: LoginFormValue) => Promise<void>;
    onMfaSubmit: (mfaCode: string) => Promise<void>;
    onMfaCancel: () => Promise<void>;
    mfaMode: MfaMode;
    loading: boolean;
    error: {
      message?: string;
      type: number | string;
      variables?: Record<string, unknown>;
    };
    autodiscovery: boolean;
    setAutoDiscovery: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
}

interface AuthLoginBody {
  username: string;
  passwordHash: string;
  application: ApplicationName.work4all20;
  mfaCode: string;
}

export const LoginPageWrapper: React.FC<LoginPageWrapperProps> = (
  props: LoginPageWrapperProps
) => {
  const { t } = useTranslation();

  const [autodiscovery, setAutoDiscovery] = useState(true);
  const db = useRxDB();
  const user = useUser();
  const [error, setError] = useState<{
    message?: string;
    type: number | string;
    variables?: Record<string, unknown>;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const [mfaMode, setMfaMode] = useState<MfaMode>(null);

  const location = useLocation();
  const _username = useRef(null);
  const _passwordHash = useRef(null);
  const _apiUrl = useRef(null);
  const _baseUrl = useRef(null);
  const _autodiscoverRes = useRef(null);
  const _mfaCode = useRef(null);
  const params = useSearchParams();
  const queryServiceAccessId = params[0].get('id');
  const queryServiceAccessCode = params[0].get('code');
  const queryServiceAccessApiUrl = params[0].get('backend');

  const { performServiceAccessLogin } = usePerformLogin();

  const unmountedRef = useRef(false);
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // Remove jumping on iOS
  useEffect(() => {
    if (Capacitor.getPlatform() === 'ios') {
      Keyboard.setResizeMode({ mode: KeyboardResize.None });
      Keyboard.addListener('keyboardWillShow', () => {
        document.getElementById('ios-scroll-fix').style.height = '200px';
      });

      Keyboard.addListener('keyboardWillHide', () => {
        document.getElementById('ios-scroll-fix').style.height = '0';
      });
      return () => {
        Keyboard.setResizeMode({ mode: KeyboardResize.Native });
        Keyboard.removeAllListeners();
      };
    }
  }, []);

  const [requiredMfa, setRequiredMfa] = useState<boolean>(false);
  const handleMfaClose = useCallback(() => {
    setRequiredMfa(false);
  }, []);

  const mfa = useRestMfa({
    apiUrl: _apiUrl.current,
    baseUrl: _baseUrl.current,
    username: _username.current,
    passwordHash: _passwordHash.current,
  });

  const getMfaViaMail = async (baseUrl: string, mailAddress: string) => {
    return fetch(baseUrl + '/api/auth/mfacode?mailAddress=' + mailAddress);
  };

  const onMfaCancel = async () => {
    setMfaMode(null);
    _mfaCode.current = '';
  };

  const onSubmit = async (values: LoginFormValue) => {
    _username.current = values.username.trim();
    _passwordHash.current = md5(values.password);

    if (autodiscovery) {
      _autodiscoverRes.current = await getAutodiscoveryBaseUrl(
        _username.current || ''
      );

      // Check if autodiscovery failed and no fallback was available
      if (
        !_autodiscoverRes.current ||
        !_autodiscoverRes.current.data ||
        _autodiscoverRes.current.data.length === 0 ||
        _autodiscoverRes.current.status !== 200
      ) {
        setError({
          type: 'AUTODISCOVERY_FAILED',
          message: t('LOGIN.MESSAGE.AUTODISCOVERY_FAILED'),
        });
        setLoading(false);
        return;
      }

      _baseUrl.current = _autodiscoverRes.current.data[0];
      _apiUrl.current = `${_baseUrl.current}/api`;
    } else {
      _apiUrl.current = values.apiUrl?.trim().replace(/\/$/, '') || '';
      _baseUrl.current = new URL(_apiUrl.current).origin;
    }
    performLogin();
  };

  const onMfaSubmit = async (mfaCode: string) => {
    _mfaCode.current = mfaCode;
    await performLogin();
    _mfaCode.current = null;
    setMfaMode(null);
  };

  const handleLoginResult = useCallback(
    async (res?: HttpClientResponse<components['schemas']['AuthResultV2']>) => {
      const baseUrl = _baseUrl.current;

      if (res.data === null) {
        setError({ message: t('LOGIN.API_NOT_FOUND'), type: 404 });
        setLoading(false);
        return;
      }

      if (res.data.mandanten?.length === 0) {
        setError({ message: t('LOGIN.NO_MANDANTEN'), type: 403 });
        setLoading(false);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = res.data as any;

      const roles: IUser['roles'] = data.currentUser.userRoles.map((role) => ({
        id: role.code,
        name: role.name,
      }));

      const user: IUser = {
        benutzerCode: data.benutzerCode,
        benutzerRechte: data.benutzerRechte,
        token: data.token,
        refreshToken: data.refreshToken,
        baseUrl,
        mandanten: data.mandanten,
        roles,
        firstName: data.currentUser.vorname,
        lastName: data.currentUser.nachname,
        shortName: data.currentUser.zeichen,
        displayName: data.currentUser.anzeigename,
        salutation: data.currentUser.briefAnrede,
        phone: data.currentUser.telefon,
        email: data.currentUser.eMail,
        departmentName: data.currentUser.abteilung,
        isMaster: data.currentUser.master,
        kundennummer: data.kundennummer,
        senderMailaddresses: data.currentUser.senderMailaddresses,
        kommtGehtMinimaleStartZeit: data.currentUser.kommtGehtMinimaleStartZeit,
        mfaMode: data.currentUser.mfaMode
          ? mfaModeByIdx[data.currentUser.mfaMode]
          : undefined,
        supplierCode: data.currentUser.lieferantenCode,
        workTimeTrackingMinBreakTime:
          data.currentUser.workTimeTrackingMinBreakTime,
        userKind:
          data.currentUser.licenseInformation?.userType?.toUpperCase() as UserClass,
      };

      const apiLicenseLock = res.headers.get(API_LICENSE_HEADER_NAME);
      if ((!canAddUser(user) || !data.currentUser.master) && apiLicenseLock) {
        const [current, max] = apiLicenseLock.split('/');
        setError({
          type: 'LICENSE_DENIED',
          variables: {
            current,
            max,
          },
        });
        setLoading(false);
        return;
      }
      updateApiLicenseLock(apiLicenseLock);

      sessionStorage.removeItem('currentTimeSettings');
      await db.upsertLocal(USER_META_ID, {
        user,
      });
    },
    [db, t]
  );

  const performLogin = useCallback(async () => {
    const baseUrl = _baseUrl.current;
    const apiUrl = _apiUrl.current;
    const username = _username.current;
    const passwordHash = _passwordHash.current;
    const autodiscoverRes = _autodiscoverRes.current;
    setError(null);
    setLoading(true);

    if (autodiscoverRes?.status !== 200 && autodiscovery) {
      setError({
        type: 'AUTODISCOVERY_FAILED',
        message: t('LOGIN.MESSAGE.AUTODISCOVERY_FAILED'),
      });
      setLoading(false);
      return;
    }
    try {
      // TODO The API response is not typed correctly after the recent changes.
      const res = await httpClient.post<
        components['schemas']['AuthResultV2'],
        AuthLoginBody
      >({
        url: `${apiUrl}/Auth/login`,
        body: {
          username,
          passwordHash,
          application: ApplicationName.work4all20,
          mfaCode: _mfaCode.current,
        },
        headers: {
          'x-work4all-apiurl': baseUrl,
        },
        contentType: 'application/json',
      });

      handleLoginResult(res);
    } catch (error) {
      console.error('Login Error:', error);

      const errorData = error?.data;
      const parsedErrorData = JSON.parse(errorData ?? '{}');

      if (!errorData) {
        const capturedExceptionId = ReactSentry.captureException(
          new Error(
            'Encountered an undefined `error.data` in the response while logging in.'
          ),
          (scope) => {
            scope.setExtra('error', error);
            return scope;
          }
        );

        setError({
          type: 'UNKNOWN',
          variables: { errorId: capturedExceptionId },
        });
      } else if (parsedErrorData?.code === 402) {
        setError({
          message: parsedErrorData?.reason,
          type: 'LICENSE_EXPIRED',
        });
      } else if (parsedErrorData?.type === 15) {
        setMfaMode(mfaModeByIdx[parsedErrorData?.mfaMode]);
        if (mfaModeByIdx[parsedErrorData?.mfaMode] === MfaMode.MAIL) {
          getMfaViaMail(baseUrl, username);
        }
      } else if (parsedErrorData?.type === 16) {
        setRequiredMfa(true);
        setLoading(false);
      } else {
        setError({
          message: parsedErrorData?.message,
          type: parsedErrorData?.type,
        });
      }
    }

    if (!unmountedRef.current) {
      setLoading(false);
    }
  }, [autodiscovery, handleLoginResult, t]);

  useEffect(() => {
    if (
      queryServiceAccessId &&
      queryServiceAccessApiUrl &&
      queryServiceAccessCode
    ) {
      _baseUrl.current = queryServiceAccessApiUrl;

      performServiceAccessLogin({
        queryServiceAccessApiUrl,
        queryServiceAccessId,
        queryServiceAccessCode,
        handleLoginResult,
      });
    }
  }, [
    performServiceAccessLogin,
    queryServiceAccessId,
    queryServiceAccessApiUrl,
    queryServiceAccessCode,
    handleLoginResult,
  ]);

  if (user) {
    const locationState = location.state as { from: Path };
    return <Navigate to={locationState?.from || '/home'} replace />;
  }

  return (
    <YSplit>
      <TakeRestHeight>
        <div data-theme="light" className={styles.login}>
          <div className={styles['login__top-block']} />
          {requiredMfa && (
            <>
              <img
                className={styles['login__bubble-icon']}
                src={Bubble}
                alt=""
              />
              <div className={styles.titleWrapper}>
                <Typography
                  className={styles['login__title']}
                  component="h4"
                  variant="h4"
                >
                  {t('LOGIN.MFA.TITLE')}
                </Typography>
              </div>

              <div className={styles['login__2fa-container']}>
                <div className={styles['login__2fa']}>
                  <Typography variant="body2" component="span">
                    {t('MFA.REQUIRED_INFO')}
                  </Typography>
                  <MfaButtons
                    currentMfaMode={MfaMode.NORMAL}
                    {...mfa}
                    onClose={handleMfaClose}
                  />
                </div>
              </div>

              <Stack className={styles['login__demo-container']}>
                <BaseActionButton
                  className={styles['login__demo-link']}
                  onClick={handleMfaClose}
                  icon={<ArrowBack />}
                >
                  <Typography
                    variant="body2"
                    component="span"
                    className={styles['login__demo-text']}
                  >
                    {t('LOGIN.MFA.BACK')}
                  </Typography>
                </BaseActionButton>
              </Stack>
            </>
          )}

          {!requiredMfa &&
            props.children({
              onSubmit,
              onMfaSubmit,
              onMfaCancel,
              mfaMode,
              loading,
              error,
              autodiscovery,
              setAutoDiscovery,
            })}
        </div>
        <div id="ios-scroll-fix" />
      </TakeRestHeight>
    </YSplit>
  );
};
